import RdvResponsePage from "@/pages/RdvResponsePage";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
// ChantierFlow App
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { OrgProvider } from "@/context/OrgContext";
import { DisputeBadgeProvider } from "@/components/admin/DisputeBadge";
import LoginPage from "@/pages/LoginPage";
import { GoogleAuthCallback } from "@/components/auth/GoogleAuthCallback";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import TeamBillingPage from "@/pages/TeamBillingPage";
import BillingPage from "@/pages/BillingPage";
import SuperAdminPage from "@/pages/SuperAdminPage";
import DashboardPage from "@/pages/DashboardPage";
import OrdersPage from "@/pages/OrdersPage";
import OrderDetailPage from "@/pages/OrderDetailPage";
import OrderTypesPage from "@/pages/OrderTypesPage";
import SellersPage from "@/pages/SellersPage";
import PlanningPage from "@/pages/PlanningPage";
import CalendarPage from "@/pages/CalendarPage";
import ReportsPage from "@/pages/ReportsPage";
import CommissionsPage from "@/pages/CommissionsPage";
import AdminPage from "@/pages/AdminPage";
import ImportPage from "@/pages/ImportPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import ObjectivesPage from "@/pages/ObjectivesPage";
import CATrackingPage from "@/pages/CATrackingPage";
import EmployeesPage from "@/pages/EmployeesPage";
import SubcontractorsPage from "@/pages/SubcontractorsPage";
import SuppliersPage from "@/pages/SuppliersPage";
import UsersPerformancePage from "@/pages/UsersPerformancePage";
import NotificationsPage from "@/pages/NotificationsPage";
import EquipmentPage from "@/pages/EquipmentPage";
import SAVPage from "@/pages/SAVPage";
import SAVMobileWorkerPage from "@/pages/SAVMobileWorkerPage";
import PWAInstaller from "@/components/PWAInstaller";
import AdminTasksPage from "@/pages/AdminTasksPage";
import ClientsPage from "@/pages/ClientsPage";
import WorkerProfitabilityPage from "@/pages/WorkerProfitabilityPage";
import AIAssistantPage from "@/pages/AIAssistantPage";
import EmailsPage from "@/pages/EmailsPage";
import ImportOrdersPage from "@/pages/ImportOrdersPage";
import ImportSuppliersPage from "@/pages/ImportSuppliersPage";
import FieldRequestsPage from "@/pages/FieldRequestsPage";
import EmployeeSubmissionsPage from "@/pages/EmployeeSubmissionsPage";
import AccountPage from "@/pages/AccountPage";
import PermissionsAdminPage from "@/pages/PermissionsAdminPage";
import AppConfigurationPage from "@/pages/AppConfigurationPage";
import LogisticsSettingsPage from "@/pages/LogisticsSettingsPage";
import EquivalencesPage from "@/pages/EquivalencesPage";
import SupplierCategoriesPage from "@/pages/SupplierCategoriesPage";
import AccountingCutOffPage from "@/pages/AccountingCutOffPage";
import Layout from "@/components/Layout";
import "@/App.css";

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  if (!isAuthenticated) {
    // Preserve original URL so we can redirect back after login (QR scan UX).
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return children;
};

// Retour de Google (#session_id=...) : traité avant toute route protégée
const GoogleHashGate = ({ children }) => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <GoogleAuthCallback />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <OrgProvider>
        <PWAInstaller />
        <BrowserRouter>
          <GoogleHashGate>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            <Route path="/rdv/:token" element={<RdvResponsePage />} />
            {/* Standalone mobile view (no sidebar) — accessible via QR code */}
            <Route
              path="/sav/intervention/:savId"
              element={
                <ProtectedRoute>
                  <SAVMobileWorkerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <DisputeBadgeProvider>
                  <Layout>
                    <Routes>
                      <Route index element={<DashboardPage />} />
                      <Route path="commandes" element={<OrdersPage />} />
                      <Route path="commandes/:id" element={<OrderDetailPage />} />
                      <Route path="types" element={<OrderTypesPage />} />
                      <Route path="vendeurs" element={<SellersPage />} />
                      <Route path="salaries" element={<EmployeesPage />} />
                      <Route path="sous-traitants" element={<SubcontractorsPage />} />
                      <Route path="fournisseurs" element={<SuppliersPage />} />
                      <Route path="planning" element={<PlanningPage />} />
                      <Route path="calendrier" element={<CalendarPage />} />
                      <Route path="graphiques" element={<AnalyticsPage />} />
                      <Route path="objectifs" element={<ObjectivesPage />} />
                      <Route path="suivi-ca" element={<CATrackingPage />} />
                      <Route path="rapports" element={<ReportsPage />} />
                      <Route path="commissions" element={<CommissionsPage />} />
                      <Route path="import" element={<ImportPage />} />
                      <Route path="admin" element={<AdminPage />} />
                      <Route path="performance" element={<UsersPerformancePage />} />
                      <Route path="notifications" element={<NotificationsPage />} />
                      <Route path="emails" element={<EmailsPage />} />
                      <Route path="import-commandes" element={<ImportOrdersPage />} />
                      <Route path="import-fournisseurs" element={<ImportSuppliersPage />} />
                      <Route path="analyse-fournisseurs" element={<Navigate to="/fournisseurs" replace />} />
                      <Route path="materiel" element={<EquipmentPage />} />
                      <Route path="sav" element={<SAVPage />} />
                      <Route path="administratif" element={<AdminTasksPage />} />
                      <Route path="comptabilite/cut-off" element={<AccountingCutOffPage />} />
                      <Route path="comptabilite" element={<Navigate to="/comptabilite/cut-off" replace />} />
                      <Route path="clients" element={<ClientsPage />} />
                      <Route path="rentabilite-intervenants" element={<WorkerProfitabilityPage />} />
                      <Route path="demandes-chantier" element={<FieldRequestsPage />} />
                      <Route path="fiches-salaries" element={<EmployeeSubmissionsPage />} />
                      <Route path="mon-compte" element={<AccountPage />} />
                      <Route path="administration/permissions" element={<PermissionsAdminPage />} />
                      <Route path="parametres/configuration" element={<AppConfigurationPage />} />
                      <Route path="parametres/logistique" element={<LogisticsSettingsPage />} />
                      <Route path="parametres/equivalences" element={<EquivalencesPage />} />
                      <Route path="parametres/categories-fournisseurs" element={<SupplierCategoriesPage />} />
                      <Route path="parametres" element={<Navigate to="/parametres/configuration" replace />} />
                      <Route path="ia" element={<AIAssistantPage />} />
                      <Route path="equipe-facturation" element={<TeamBillingPage />} />
                      <Route path="facturation" element={<BillingPage />} />
                      <Route path="super-admin" element={<SuperAdminPage />} />
                    </Routes>
                  </Layout>
                  </DisputeBadgeProvider>
                </ProtectedRoute>
              }
            />
          </Routes>
          </GoogleHashGate>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </OrgProvider>
    </AuthProvider>
  );
}

export default App;
